/*
Created-by: XtroEdge AI
Updated-by: XtroEdge AI
Last modified: 2026-11-03T23:40:00+05:00
Intent: Pure helpers for the Receipt level type: form value <-> API receipt_rules (a renamed product drops its old SKU; each product can carry an Arabic name for Arabic participants), plus client-side validation that mirrors the backend rules and checks the rules against the level dates and the campaign countries' currencies.
*/
import { AbstractControl, ValidationErrors, ValidatorFn } from '@angular/forms';
import { isCurrencyCode } from 'src/app/utils/country-currency.util';

/** Limits the backend enforces (src/shared/common/receipt.ts). */
export const RECEIPT_LIMITS = {
  maxRetailers: 50,
  maxProducts: 100,
  maxAliases: 20,
  maxText: 120,
  maxQuantity: 10000,
  maxSpend: 1_000_000,
  minAttempts: 1,
  maxAttempts: 10,
} as const;

export type ReceiptSpendBasis = 'receipt_total' | 'qualifying_items';
export type ReceiptCombinationMode = 'any' | 'all';

/** What the form edits. Text areas / inputs, not the final API shape. */
export interface ReceiptRulesFormValue {
  retailers_text: string;
  /**
   * `sku_name` is the name the saved SKU was made for. Not sent: a product renamed to something
   * else drops the old SKU so the server issues one for the new name.
   * `arabic_name` is what Arabic participants see; empty shows them `name`.
   */
  products: { name: string; arabic_name?: string; aliases_text: string; sku: string; sku_name?: string }[];
  min_quantity: number | string | null;
  min_spend: number | string | null;
  /** Currencies a receipt may be in. Empty means the campaign countries' currencies. */
  currencies: string[] | null;
  /**
   * The currencies of the campaign's countries. Not sent: kept beside the rules so the
   * validator can refuse a currency the campaign does not use, and the save can default to them.
   */
  allowed_currencies?: string[] | null;
  spend_basis: ReceiptSpendBasis;
  combination_mode: ReceiptCombinationMode;
  purchase_from: string | null;
  purchase_to: string | null;
  max_attempts: number | string | null;
  /** Not editable here; carried through when the backend returned a deliberate value. */
  confidence_threshold?: number | null;
}

export interface ReceiptProductPayload {
  name: string;
  arabic_name?: string;
  aliases: string[];
  sku?: string;
}

/** The shape the backend reads from `receipt_rules` on each receipt level. */
export interface ReceiptRulesPayload {
  retailers: string[];
  /** `arabic_name` only when one was typed. */
  products: ReceiptProductPayload[];
  min_quantity: number;
  min_spend: number;
  /** The one currency when there is exactly one; null when several are accepted (see currencies). */
  currency: string | null;
  /** Every accepted currency. Absent only when no currency is known (any currency). */
  currencies?: string[];
  spend_basis: ReceiptSpendBasis;
  combination_mode: ReceiptCombinationMode;
  purchase_from: string | null;
  purchase_to: string | null;
  max_attempts: number;
  confidence_threshold?: number;
}

export const RECEIPT_DEFAULT_ATTEMPTS = 3;

/**
 * How sure the receipt reader must be before a receipt is approved without a person looking at
 * it. The PRD sets 0.85. The backend used to fill in 0.7 when none was sent, which approved more
 * unclear receipts on its own, so that old default is not carried forward.
 */
export const RECEIPT_DEFAULT_CONFIDENCE = 0.85;
const LEGACY_DEFAULT_CONFIDENCE = 0.7;

export function defaultReceiptRulesForm(): ReceiptRulesFormValue {
  return {
    retailers_text: '',
    products: [],
    min_quantity: 0,
    min_spend: 0,
    currencies: [],
    spend_basis: 'receipt_total',
    combination_mode: 'any',
    purchase_from: null,
    purchase_to: null,
    max_attempts: RECEIPT_DEFAULT_ATTEMPTS,
  };
}

/** Validation result: a translation key suffix per field (under add-content.receipt.err.*). */
export interface ReceiptRulesErrors {
  retailers?: string;
  products?: string;
  /** Per product row index. */
  productRows?: Record<number, string>;
  min_quantity?: string;
  min_spend?: string;
  currency?: string;
  spend_basis?: string;
  purchase_from?: string;
  purchase_to?: string;
  max_attempts?: string;
  /** Values for the message of a field ('purchase_to') or a product row ('product.1'). */
  params?: Record<string, Record<string, string | number>>;
}

/** What the rules are checked against beyond themselves: the level they belong to. */
export interface ReceiptRulesContext {
  levelStart?: unknown;
  levelEnd?: unknown;
}

const DATE_RE = /^\d{4}-\d{2}-\d{2}$/;

/** One entry per non-empty line, trimmed, case-insensitive duplicates dropped. */
export function splitLines(text: string | null | undefined): string[] {
  return uniq(String(text ?? '').split(/\r?\n/));
}

/** Aliases are typed comma separated (Latin or Arabic comma); a new line works too. */
export function splitAliases(text: string | null | undefined): string[] {
  return uniq(String(text ?? '').split(/[,،\n\r]+/));
}

function uniq(items: string[]): string[] {
  const out: string[] = [];
  const seen = new Set<string>();
  for (const item of items) {
    const s = String(item ?? '').trim();
    const key = s.toLowerCase();
    if (s && !seen.has(key)) {
      seen.add(key);
      out.push(s);
    }
  }
  return out;
}

/** A real calendar date as YYYY-MM-DD, or null. Keeps the date part of a timestamp. */
export function toIsoDate(value: unknown): string | null {
  if (value === null || value === undefined || value === '') return null;
  const head = String(value).trim().slice(0, 10);
  if (!DATE_RE.test(head)) return null;
  const d = new Date(head + 'T00:00:00Z');
  return !Number.isNaN(d.getTime()) && d.toISOString().slice(0, 10) === head ? head : null;
}

/** The local calendar day of a level date (a Date, a timestamp or a date string), or null. */
function localDay(value: unknown): string | null {
  if (value === null || value === undefined || value === '') return null;
  const d = value instanceof Date ? value : new Date(value as any);
  if (Number.isNaN(d.getTime())) return null;
  const pad = (n: number) => String(n).padStart(2, '0');
  return `${d.getFullYear()}-${pad(d.getMonth() + 1)}-${pad(d.getDate())}`;
}

/** YYYY-MM-DD -> DD/MM/YYYY, the way the date pickers show it. */
function displayDay(day: string): string {
  const [y, m, d] = day.split('-');
  return `${d}/${m}/${y}`;
}

function numberOr(value: unknown, fallback: number): number {
  if (value === null || value === undefined || String(value).trim() === '') return fallback;
  return Number(value);
}

function isBlank(value: unknown): boolean {
  return value === null || value === undefined || String(value).trim() === '';
}

/** Upper case, trimmed, blanks and repeats dropped. A single string is read as one code. */
export function cleanCurrencies(value: unknown): string[] {
  const list = Array.isArray(value) ? value : isBlank(value) ? [] : [value];
  return uniq(list.map((c) => String(c ?? '').trim().toUpperCase()));
}

/** A row with nothing typed in it, which is ignored. */
function isEmptyProduct(p: ReceiptRulesFormValue['products'][number] | null | undefined): boolean {
  return [p?.name, p?.arabic_name, p?.aliases_text].every((v) => String(v ?? '').trim() === '');
}

/** Products that count: a row needs a name; a fully empty row is ignored. */
function filledProducts(form: ReceiptRulesFormValue) {
  return (form.products || []).filter((p) => !isEmptyProduct(p));
}

/** Number of products with a name (what the backend counts). */
export function receiptProductCount(form: ReceiptRulesFormValue): number {
  return (form.products || []).filter((p) => String(p?.name ?? '').trim() !== '').length;
}

/** The currencies the save sends: the admin's choice, else the campaign countries' currencies. */
export function effectiveCurrencies(form: ReceiptRulesFormValue): string[] {
  const chosen = cleanCurrencies(form.currencies);
  return chosen.length ? chosen : cleanCurrencies(form.allowed_currencies);
}

/** API receipt_rules (any older/lenient shape) -> form value. */
export function receiptRulesToForm(rules: any): ReceiptRulesFormValue {
  const base = defaultReceiptRulesForm();
  if (!rules || typeof rules !== 'object') return base;

  const retailers = Array.isArray(rules.retailers)
    ? rules.retailers
    : typeof rules.retailers === 'string'
      ? rules.retailers.split(/\r?\n/)
      : [];
  const products = (Array.isArray(rules.products) ? rules.products : []).map((p: any) => {
    const isObj = p && typeof p === 'object';
    const aliases = isObj && Array.isArray(p.aliases) ? p.aliases : [];
    const name = String((isObj ? p.name : p) ?? '');
    const sku = isObj && p.sku ? String(p.sku) : '';
    return {
      name,
      arabic_name: String((isObj ? p.arabic_name ?? p.name_ar ?? p.ar_name : '') ?? ''),
      aliases_text: aliases.map((a: any) => String(a ?? '')).join(', '),
      sku,
      sku_name: sku ? name : '',
    };
  });

  const currencies = cleanCurrencies(rules.currencies);
  const form: ReceiptRulesFormValue = {
    retailers_text: retailers.map((r: any) => String(r ?? '')).join('\n'),
    products,
    min_quantity: numberOr(rules.min_quantity, 0),
    min_spend: numberOr(rules.min_spend, 0),
    currencies: currencies.length ? currencies : cleanCurrencies(rules.currency),
    spend_basis: rules.spend_basis === 'qualifying_items' ? 'qualifying_items' : 'receipt_total',
    combination_mode: rules.combination_mode === 'all' ? 'all' : 'any',
    purchase_from: toIsoDate(rules.purchase_from),
    purchase_to: toIsoDate(rules.purchase_to),
    max_attempts: numberOr(rules.max_attempts, RECEIPT_DEFAULT_ATTEMPTS),
  };
  const confidence = Number(rules.confidence_threshold);
  if (!isBlank(rules.confidence_threshold) && Number.isFinite(confidence)
    && confidence !== LEGACY_DEFAULT_CONFIDENCE) {
    form.confidence_threshold = confidence;
  }
  return form;
}

/**
 * Whether a saved SKU still belongs to the product. The server makes the SKU from the name
 * ('ITRANOX 15CAP' -> 'itranox-15cap'), so a product renamed to 'Beige item' must not keep it.
 * Spacing and letter case do not count as a rename. Unknown origin (no sku_name) keeps it.
 */
function skuStillFits(name: string, skuName: string | undefined): boolean {
  if (skuName === undefined || skuName === null) return true;
  const key = (value: string) => String(value ?? '').trim().replace(/\s+/g, ' ').toLowerCase();
  return key(name) === key(skuName);
}

/** Form value -> the `receipt_rules` object the save reads. Never throws (drafts save unvalidated). */
export function buildReceiptRules(form: ReceiptRulesFormValue): ReceiptRulesPayload {
  const products = (form.products || [])
    .map((p) => {
      const name = String(p?.name ?? '').trim();
      const arabicName = String(p?.arabic_name ?? '').trim();
      const sku = String(p?.sku ?? '').trim();
      const item: ReceiptProductPayload = { name, aliases: splitAliases(p?.aliases_text) };
      if (arabicName) item.arabic_name = arabicName;
      // The SKU follows the English name only; an Arabic name added or changed keeps it.
      if (sku && skuStillFits(name, p?.sku_name)) item.sku = sku;
      return item;
    })
    .filter((p) => p.name !== '');

  const currencies = effectiveCurrencies(form);
  const payload: ReceiptRulesPayload = {
    retailers: splitLines(form.retailers_text),
    products,
    min_quantity: numberOr(form.min_quantity, 0),
    min_spend: numberOr(form.min_spend, 0),
    currency: currencies.length === 1 ? currencies[0] : null,
    // With no products there is nothing to count spend on but the whole receipt.
    spend_basis: products.length && form.spend_basis === 'qualifying_items' ? 'qualifying_items' : 'receipt_total',
    // Only meaningful with several products; keep the stored value tidy otherwise.
    combination_mode: products.length > 1 && form.combination_mode === 'all' ? 'all' : 'any',
    purchase_from: toIsoDate(form.purchase_from),
    purchase_to: toIsoDate(form.purchase_to),
    max_attempts: numberOr(form.max_attempts, RECEIPT_DEFAULT_ATTEMPTS),
    confidence_threshold: typeof form.confidence_threshold === 'number' && Number.isFinite(form.confidence_threshold)
      ? form.confidence_threshold
      : RECEIPT_DEFAULT_CONFIDENCE,
  };
  if (currencies.length) payload.currencies = currencies;
  return payload;
}

/**
 * Mirrors normalizeReceiptRules in the backend, plus what only the form can know: the level's
 * dates and the campaign's currencies. Returns {} when the rules are fine.
 */
export function validateReceiptRules(form: ReceiptRulesFormValue, context: ReceiptRulesContext = {}): ReceiptRulesErrors {
  const errors: ReceiptRulesErrors = {};
  const params: Record<string, Record<string, string | number>> = {};
  const L = RECEIPT_LIMITS;

  const retailers = splitLines(form.retailers_text);
  if (retailers.length > L.maxRetailers) errors.retailers = 'retailersTooMany';
  else if (retailers.some((r) => r.length > L.maxText)) errors.retailers = 'retailersTooLong';

  const rows = filledProducts(form);
  if (rows.length > L.maxProducts) errors.products = 'productsTooMany';
  const productRows: Record<number, string> = {};
  // Each name, and each other name, may belong to one product only: a receipt line that matches
  // two products would be counted for whichever the reader happened to pick.
  const owner = new Map<string, number>();
  (form.products || []).forEach((p, index) => {
    if (isEmptyProduct(p)) return;
    const name = String(p?.name ?? '').trim();
    const arabicName = String(p?.arabic_name ?? '').trim();
    const aliases = splitAliases(p?.aliases_text);
    if (!name) productRows[index] = 'productNameRequired';
    else if (name.length > L.maxText) productRows[index] = 'productNameTooLong';
    else if (arabicName.length > L.maxText) productRows[index] = 'productArabicNameTooLong';
    else if (aliases.length > L.maxAliases) productRows[index] = 'aliasesTooMany';
    else if (aliases.some((a) => a.length > L.maxText)) productRows[index] = 'aliasTooLong';

    // Two products with one Arabic name would look the same to an Arabic participant.
    const terms = uniq([name, arabicName, ...aliases].filter(Boolean));
    for (const term of terms) {
      const key = term.toLowerCase();
      const first = owner.get(key);
      if (first === undefined) {
        owner.set(key, index);
      } else if (!productRows[index]) {
        productRows[index] = 'nameUsedTwice';
        params['product.' + index] = { name: term, product: first + 1 };
      }
    }
  });
  if (Object.keys(productRows).length) errors.productRows = productRows;

  const productCount = receiptProductCount(form);

  const minQuantity = numberOr(form.min_quantity, 0);
  if (!Number.isInteger(minQuantity) || minQuantity < 0 || minQuantity > L.maxQuantity) {
    errors.min_quantity = 'quantityRange';
  } else if (minQuantity > 0 && productCount === 0) {
    errors.min_quantity = 'quantityNeedsProduct';
  }

  const minSpend = numberOr(form.min_spend, 0);
  if (!(minSpend >= 0 && minSpend <= L.maxSpend)) {
    errors.min_spend = 'spendRange';
  }
  if (form.spend_basis === 'qualifying_items' && productCount === 0) {
    errors.spend_basis = 'basisNeedsProduct';
  }

  const currencies = cleanCurrencies(form.currencies);
  const allowed = cleanCurrencies(form.allowed_currencies);
  const unknown = currencies.find((c) => !isCurrencyCode(c));
  const foreign = currencies.find((c) => allowed.length > 0 && !allowed.includes(c));
  if (unknown) {
    errors.currency = 'currencyFormat';
  } else if (foreign) {
    errors.currency = 'currencyNotInCampaign';
    params['currency'] = { currency: foreign, currencies: allowed.join(', ') };
  }

  const fromRaw = form.purchase_from ? String(form.purchase_from).trim() : '';
  const toRaw = form.purchase_to ? String(form.purchase_to).trim() : '';
  const from = toIsoDate(fromRaw);
  const to = toIsoDate(toRaw);
  if (fromRaw && !from) errors.purchase_from = 'dateInvalid';
  if (toRaw && !to) errors.purchase_to = 'dateInvalid';
  if (from && to && from > to) errors.purchase_to = 'dateOrder';

  // A window that closes before the level opens, or opens after it closes, lets no receipt
  // through at all: every receipt a player could upload during the level would be refused.
  const levelStart = localDay(context.levelStart);
  const levelEnd = localDay(context.levelEnd);
  if (to && !errors.purchase_to && levelStart && to < levelStart) {
    errors.purchase_to = 'purchaseEndsBeforeLevel';
    params['purchase_to'] = { date: displayDay(levelStart) };
  }
  if (from && !errors.purchase_from && levelEnd && from > levelEnd) {
    errors.purchase_from = 'purchaseStartsAfterLevel';
    params['purchase_from'] = { date: displayDay(levelEnd) };
  }

  const attempts = isBlank(form.max_attempts) ? NaN : Number(form.max_attempts);
  if (!Number.isInteger(attempts) || attempts < L.minAttempts || attempts > L.maxAttempts) {
    errors.max_attempts = 'attemptsRange';
  }

  if (Object.keys(params).length) errors.params = params;
  return errors;
}

export function hasReceiptErrors(errors: ReceiptRulesErrors | null | undefined): boolean {
  return !!errors && Object.keys(errors).some((key) => key !== 'params');
}

/**
 * Whether the admin typed anything that a type switch would throw away. Defaults
 * (basis, mode, attempts, the campaign's own currencies) and zeroes do not count.
 */
export function receiptRulesHaveData(form: ReceiptRulesFormValue | null | undefined): boolean {
  if (!form) return false;
  if (String(form.retailers_text ?? '').trim()) return true;
  if (filledProducts(form).length) return true;
  if (Number(form.min_quantity) > 0 || Number(form.min_spend) > 0) return true;
  const currencies = cleanCurrencies(form.currencies);
  const allowed = cleanCurrencies(form.allowed_currencies);
  if (currencies.length && currencies.join() !== allowed.join()) return true;
  return !!(form.purchase_from || form.purchase_to);
}

/**
 * Group validator for a level's receipt_rules. Sets { receiptRules: ReceiptRulesErrors } so the
 * template and the save summary can read which field is wrong. A standalone function (not a class
 * field) so it also works on components built without running field initialisers. The level's
 * dates are read from the level the rules sit in.
 */
export const receiptRulesValidator: ValidatorFn = (control: AbstractControl): ValidationErrors | null => {
  const level = control.parent;
  const errors = validateReceiptRules((control as any).getRawValue() as ReceiptRulesFormValue, {
    levelStart: level?.get('level_start_date')?.value,
    levelEnd: level?.get('level_timeline')?.value,
  });
  return hasReceiptErrors(errors) ? { receiptRules: errors } : null;
};
